import React, { useState } from 'react';
// @ts-ignore
import Modal from '../../shared/modal';
import { LocalizationContextType } from '../../shared/contexts'
import { formatTenantDateTime } from '../../../utils/formatTenantDateTime'
import { TIME_SOURCE, timeSourceTitle } from '../../../utils/timeSource'

interface User {
  id: number;
  email: string;
  name: string;
}

interface AuditableDisplay {
  display_name: string;
  class_name: string;
  id: number;
}

interface Changes {
  type: string;
  changes?: Record<string, { from: any; to: any }>;
  new_values?: Record<string, any>;
  previous_values?: Record<string, any>;
  changed_fields: string[];
  summary: string;
}

interface AuditLog {
  id: number;
  action: string;
  auditable_type: string;
  auditable_id: number;
  user: User;
  changes: Changes;
  created_at: string;
  remote_address?: string;
  auditable_display?: AuditableDisplay;
}

interface AuditLogsTableProps {
  audit_logs: AuditLog[];
  localization_context?: LocalizationContextType;
}

const AuditLogsTable: React.FC<AuditLogsTableProps> = ({ audit_logs, localization_context }) => {
  const [selectedAudit, setSelectedAudit] = useState<AuditLog | null>(null);

  const formatDate = (dateString: string) => formatTenantDateTime(dateString, localization_context);

  const getActionBadgeClass = (action: string) => {
    switch (action) {
      case 'create': return 'badge bg-success';
      case 'update': return 'badge bg-warning text-dark';
      case 'destroy': return 'badge bg-danger';
      default: return 'badge bg-secondary';
    }
  };

  const renderChanges = (audit: AuditLog) => {
    const { action, changes } = audit;

    if (!changes || !changes.changed_fields || changes.changed_fields.length === 0) {
      return <span></span>;
    }

    const fieldsToShow = changes.changed_fields.slice(0, 3);
    const remainingCount = changes.changed_fields.length - 3;

    return (
      <div className="small">
        <button
          type="button"
          className="btn btn-link p-0 text-start small"
          onClick={() => setSelectedAudit(audit)}
        >
          {action === 'create' && (
            <span className="text-success">Created with {changes.changed_fields.length} fields</span>
          )}
          {action === 'update' && (
            <>
              <div><strong>Changed:</strong> {fieldsToShow.join(', ')}</div>
              {remainingCount > 0 && (
                <div className="text-muted">...and {remainingCount} more</div>
              )}
            </>
          )}
          {action === 'destroy' && (
            <span className="text-danger">Deleted object</span>
          )}
          {!['create', 'update', 'destroy'].includes(action) && (
            <span className="text-muted">{action} operation</span>
          )}
        </button>
      </div>
    );
  };

  const renderChangesModal = () => {
    if (!selectedAudit) return null;

    const { changes, action, auditable_type, auditable_display } = selectedAudit;

    return (
      <Modal
        headerText={`${action.charAt(0).toUpperCase() + action.slice(1)} Details - ${auditable_type}`}
        onClose={() => setSelectedAudit(null)}
        widen={true}
      >
        <div className="audit-changes-detail">
          <div className="mb-3">
            <strong>Object ID:</strong> <span>#{selectedAudit.auditable_id}</span>
          </div>

          {action === 'create' && changes.new_values && (
            <div>
              <h6>Created with values:</h6>
              <div className="table-responsive">
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>Field</th>
                      <th>Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(changes.new_values).map(([field, value]) => (
                      <tr key={field}>
                        <td><strong>{field}</strong></td>
                        <td><code>{String(value)}</code></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {action === 'update' && changes.changes && (
            <div>
              <h6>Changed fields:</h6>
              <div className="table-responsive">
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>Field</th>
                      <th>Before</th>
                      <th>After</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(changes.changes).map(([field, change]) => (
                      <tr key={field}>
                        <td><strong className="text-warning">{field}</strong></td>
                        <td><code className="text-muted">{String(change.from)}</code></td>
                        <td><code className="text-success">{String(change.to)}</code></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {action === 'destroy' && changes.previous_values && (
            <div>
              <h6>Deleted object had values:</h6>
              <div className="table-responsive">
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>Field</th>
                      <th>Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(changes.previous_values).map(([field, value]) => (
                      <tr key={field}>
                        <td><strong>{field}</strong></td>
                        <td><code>{String(value)}</code></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </Modal>
    );
  };

  return (
    <>
      <table className="table table-robis">
        <thead className="thead-robis">
          <tr>
            <th scope="col">Date & Time</th>
            <th scope="col">Action</th>
            <th scope="col">User</th>
            <th scope="col">Model</th>
            <th scope="col">Object ID</th>
            <th scope="col">Changes</th>
          </tr>
        </thead>
        <tbody>
          {audit_logs.map((audit) => (
            <tr key={audit.id}>
              <td>
                <div className="small" title={timeSourceTitle(audit.created_at, TIME_SOURCE.app)}>
                  {formatDate(audit.created_at)}
                </div>
              </td>
              <td>
                <span className={getActionBadgeClass(audit.action)}>
                  {audit.action?.charAt(0)?.toUpperCase() + audit?.action?.slice(1)}
                </span>
              </td>
              <td>
                <div>
                  <div className="fw-bold small">{audit.user.name}</div>
                  <div className="text-muted small">{audit.user.email}</div>
                </div>
              </td>
              <td>
                <span className="badge bg-light text-dark">
                  {audit.auditable_type}
                </span>
              </td>
              <td>
                <div className="small">
                  <span className="badge bg-secondary">#{audit.auditable_id}</span>
                </div>
              </td>
              <td>
                {renderChanges(audit)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {renderChangesModal()}
    </>
  );
};

export default AuditLogsTable;
